import usePrefersReducedMotion from 'hooks/usePrefersReducedMotion'
import React, { useEffect, useRef } from 'react'
import { startHeroTimeline } from './heroAnimation/heroTimeline'
import HERO_MARKUP from './heroAnimation/markup'
import HERO_STYLES from './heroAnimation/styles'

// Id of the single injected <style> element. Module-level so remounting the
// component reuses the stylesheet rather than appending a second copy.
const STYLE_ELEMENT_ID = 'of-hero-anim-styles'
// The scope class every rule in HERO_STYLES is prefixed with.
export const HERO_SCOPE_CLASS = 'of-hero-anim'

// Injects the prototype stylesheet once per document.
//
// context:
//   It is a raw stylesheet rather than Styletron rules because it is copied
//   from the design rather than authored here — keeping it as one verbatim
//   blob makes re-copying it a diff instead of a rewrite. Styletron remains
//   the right tool for styles this app actually owns.
const useHeroStyles = (): void => {
  useEffect(() => {
    if (document.getElementById(STYLE_ELEMENT_ID)) return
    const el = document.createElement('style')
    el.id = STYLE_ELEMENT_ID
    el.textContent = HERO_STYLES
    document.head.appendChild(el)
  }, [])
}

// The Order feedback Overview hero animation.
//
// context:
//   Markup, stylesheet and cue times are copied from the pinned prototype
//   (order-feedback-landing-page/v2 v67) rather than reconstructed. The
//   previous hand-built version deliberately dropped the flying card, the
//   tap-ripple and the typing caret, and still drifted from the design twice
//   on geometry alone — copying is what keeps this in step with design.
//   The tree is inert decoration: aria-hidden, and the controls inside it are
//   presentational rather than focusable.
const HeroIllustration = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  useHeroStyles()
  // Shared with Hero.tsx two components up — subscribes to OS changes rather
  // than reading matchMedia once at mount, so a mid-session toggle restarts
  // the timeline with the new setting instead of waiting for a remount.
  const reducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    const root = containerRef.current
    if (!root) return undefined
    return startHeroTimeline(root, { reducedMotion })
  }, [reducedMotion])

  return (
    <div
      ref={containerRef}
      className={HERO_SCOPE_CLASS}
      aria-hidden="true"
      data-testid="order-feedback-hero-illustration"
      // Build-time constant copied from the design — never user- or
      // API-supplied, so there is no untrusted input to sanitise here.
      dangerouslySetInnerHTML={{ __html: HERO_MARKUP }}
    />
  )
}

export default HeroIllustration
